import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Image as ImageIcon, Upload, Lock } from "lucide-react";

/**
 * Logo uploader for the PDF cover.
 *
 * Paywall integration:
 * - When paywall is active AND the brief has NOT been paid for this assessment,
 *   the button renders in a disabled "Paid feature" state with a lock + helper text.
 * - Caller passes `access` (from useAccess(id)) and `onUpgradeClick` so the user
 *   can route to the upgrade modal in one click.
 */
export default function LogoUploader({ assessmentId, access, onUpgradeClick }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(0);

  const locked = !!access?.paywall_active && !access?.paid_brief;

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) {
      toast.error("Please upload a PNG or JPEG file.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo must be 2MB or smaller.");
      return;
    }
    const form = new FormData();
    form.append("file", f);
    try {
      setUploading(true);
      await api.post(`/assessment/${assessmentId}/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded — it will appear on your PDF cover.");
      setPreviewVersion((v) => v + 1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const previewUrl = `${API}/assessment/${assessmentId}/logo?v=${previewVersion}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-teal-600" />
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Branding · PDF cover (optional)</p>
        {locked && (
          <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-[10px] uppercase tracking-[0.12em] border border-amber-200 dark:border-amber-900/50">
            <Lock className="h-2.5 w-2.5" /> Paid
          </span>
        )}
      </div>
      <h3 className="mt-2 font-display text-base font-semibold text-slate-900">Add your logo</h3>
      <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
        {locked
          ? "Branded logo on the PDF cover is part of the paid Continuity Brief. Unlock to upload."
          : "PNG or JPEG, up to 2MB. Appears on the cover page of every PDF download or email."}
      </p>

      <div className="mt-4 flex items-start gap-4">
        {!locked && previewVersion > 0 && (
          <div className="h-16 w-32 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
            <img
              key={previewVersion}
              src={previewUrl}
              alt="Logo preview"
              data-testid="logo-preview"
              className="max-h-full max-w-full object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            onChange={onChange}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            data-testid="logo-input"
            disabled={locked}
          />
          {locked ? (
            <button
              onClick={onUpgradeClick}
              data-testid="logo-locked-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-amber-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-amber-500 transition-colors"
            >
              <Lock className="h-4 w-4" /> Unlock to upload logo
            </button>
          ) : (
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="logo-upload-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload logo"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
